/* eslint-disable @next/next/no-img-element */
import Head from 'next/head';
import SectionBlock from '../ui/section-block';
import { DEFAULT_SECTION_STYLE, type TSectionStyles } from '@/types/dynamic-sections';
import {
  type HeroSlide,
  type HeroSliderParams,
  type HeroSource,
  resolveHeroSources,
} from './hero-sources';

type HeroSlideFallbackProps = {
  title?: string;
  styles?: TSectionStyles;
  sliders?: HeroSlide[];
  params?: HeroSliderParams;
  renderByBreakpoint: boolean;
  /** Preload + high fetch priority for the LCP candidate (first home slider). */
  priority?: boolean;
  /** Section-stable id so aspect-ratio CSS cannot collide across sliders. */
  uniqueKey: string;
};

/** CSS class fragment: letters, digits, _ and - only. */
function cssSafeClassSuffix(raw: string): string {
  const cleaned = raw.replace(/[^a-zA-Z0-9_-]/g, '-').replace(/^-+|-+$/g, '');
  return cleaned || 'hero';
}

/**
 * CSS aspect-ratio must match the *visible* breakpoint art. Using only the
 * mobile `<img>` width/height on desktop reserves a tall box (~690/356) while
 * the live Swiper paints the short desktop slide (~1442/409) — a large CLS.
 */
function aspectRatioCss(className: string, sources: HeroSource[]): string {
  const [base, ...rest] = sources;
  const rules: string[] = [
    `.${className}{aspect-ratio:${base.width}/${base.height};width:100%}`,
  ];
  for (const source of rest) {
    if (!source.media) continue;
    rules.push(
      `@media ${source.media}{.${className}{aspect-ratio:${source.width}/${source.height}}}`,
    );
  }
  return rules.join('');
}

/**
 * Server-rendered first slide of a home image slider so the image (and its
 * aspect ratio) is in the initial HTML. Uses the same raw URLs as `ImageSlider`,
 * so the client slider reuses the cached file when it takes over.
 */
export function HeroSlideFallback({
  title,
  styles = DEFAULT_SECTION_STYLE,
  sliders,
  params,
  renderByBreakpoint,
  priority = false,
  uniqueKey,
}: Readonly<HeroSlideFallbackProps>) {
  const sources = resolveHeroSources({ sliders, params, renderByBreakpoint });
  if (!sources.length) return null;

  const [base, ...wider] = sources;
  const widestFirst = [...wider].reverse();
  const frameClass = `hero-slide-ar-${cssSafeClassSuffix(uniqueKey)}`;

  return (
    <>
      {priority ? (
        <Head>
          {sources.map((source) => (
            <link
              key={`hero-preload-${source.src}`}
              rel="preload"
              as="image"
              href={source.src}
              media={source.media}
              fetchPriority="high"
            />
          ))}
        </Head>
      ) : null}
      <SectionBlock title={title} style={styles?.title}>
        <div className="bg-gray-100">
          <div className="relative lg:max-w-[1560px] mx-auto">
            <style dangerouslySetInnerHTML={{ __html: aspectRatioCss(frameClass, sources) }} />
            <div className={frameClass}>
              <picture>
                {widestFirst.map((source) => (
                  <source
                    key={source.src}
                    media={source.media}
                    srcSet={source.src}
                    width={source.width}
                    height={source.height}
                  />
                ))}
                <img
                  src={base.src}
                  alt={base.alt}
                  width={base.width}
                  height={base.height}
                  fetchPriority={priority ? 'high' : undefined}
                  loading={priority ? 'eager' : 'lazy'}
                  style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    borderRadius: '24px',
                  }}
                />
              </picture>
            </div>
          </div>
          {/* Same footprint as the slider's pagination row. */}
          <div className="mt-2 h-2" aria-hidden="true" />
        </div>
      </SectionBlock>
    </>
  );
}
